import { Field, Form, Formik } from "formik"
import FormikField from "../Components/Forms/FormikField"
import Button from "../Components/Button"
import OtpInput from "../Components/Forms/OtpInput"
import { useAtom } from "jotai"
import { userAtom } from "../state"
import api from "../services/api"
import { phoneErrorMessage } from "../services/auth"
import { useNavigate } from "react-router-dom"
import { useState, useEffect } from "react"

const Login = () => {
  const [user, setUser] = useAtom(userAtom)
  const navigate = useNavigate()
  const [magicLinkSent, setMagicLinkSent] = useState(false)
  const [magicLinkEmail, setMagicLinkEmail] = useState("")

  const [phoneEnabled, setPhoneEnabled] = useState(false)
  const [phone, setPhone] = useState("")
  const [phoneCodeSent, setPhoneCodeSent] = useState(false)
  const [phoneCode, setPhoneCode] = useState("")
  const [phoneError, setPhoneError] = useState(null)
  const [phoneBusy, setPhoneBusy] = useState(false)

  useEffect(() => {
    api
      .getConfig()
      .then(response => setPhoneEnabled(response.data.phone_login_enabled))
      .catch(() => setPhoneEnabled(false))
  }, [])

  const handlePhoneRequest = event => {
    event.preventDefault()
    setPhoneBusy(true)
    setPhoneError(null)
    api
      .requestPhoneLogin(phone)
      .then(() => setPhoneCodeSent(true))
      .catch(error =>
        setPhoneError(phoneErrorMessage(error, "phone", "We couldn't send a code to that number.")),
      )
      .finally(() => setPhoneBusy(false))
  }

  const handlePhoneVerify = event => {
    event.preventDefault()
    setPhoneBusy(true)
    setPhoneError(null)
    api
      .verifyPhoneLogin(phone, phoneCode)
      .then(response => {
        setUser(response.data.user)
        navigate("/")
      })
      .catch(error =>
        setPhoneError(phoneErrorMessage(error, "code", "That code is incorrect or has expired.")),
      )
      .finally(() => setPhoneBusy(false))
  }

  const handleMagicLinkSubmit = (values, { setErrors }) => {
    api
      .requestMagicLink(values.email)
      .then(response => {
        setMagicLinkSent(true)
        setMagicLinkEmail(values.email)
      })
      .catch(error => {
        setErrors(error.response?.data?.errors || { email: ["Failed to send login link. Verify that the email is valid."] })
      })
  }

  return (
    <div className="flex min-h-screen flex-col justify-between bg-background">
      <div className="mx-auto w-full max-w-5xl p-12">
        <img className="mb-16 w-[300px]" src="/logo.png" alt="Encircled Logo"/>
        <div className="flex flex-col gap-8 md:flex-row md:gap-16">
          <div className="flex-1">
            <div className="mb-12">
              <h1 className="mb-8 text-xl font-bold text-black">Login</h1>
            </div>

            {magicLinkSent ? (
              <div className="mb-6 rounded-md border border-green-200 bg-green-50 p-6">
                <h2 className="mb-2 text-lg font-bold">Check your email</h2>
                <p className="mb-2 text-sm text-gray-700">
                  We sent a login link to <strong>{magicLinkEmail}</strong>
                </p>
                <p className="text-sm text-gray-600">
                  Click the link in the email to login. The link will expire in 15 minutes.
                </p>
                <button
                  onClick={() => setMagicLinkSent(false)}
                  className="mt-4 text-sm text-brand underline"
                >
                  Send another link
                </button>
              </div>
            ) : (
              <div className="mb-8">
                <Formik initialValues={{ email: "" }} onSubmit={handleMagicLinkSubmit}>
                  <Form>
                    <FormikField name="email" title="Email" instructions={[]}>
                      <Field
                        name="email"
                        type="email"
                        className="mb-4 w-full rounded-md border-0 bg-neutral-100 p-4"
                        placeholder="Enter your email"
                      />
                    </FormikField>
                    <Button type="submit">Send Login Link</Button>
                  </Form>
                </Formik>
              </div>
            )}

            {phoneEnabled && (
              <div className="border-t border-neutral-300 pt-8">
                <h2 className="mb-4 text-lg font-bold">Or log in with your phone</h2>
                {phoneCodeSent ? (
                  <form onSubmit={handlePhoneVerify}>
                    <p className="mb-4 text-sm text-gray-700">
                      We sent a code to <strong>{phone}</strong>
                    </p>
                    <OtpInput value={phoneCode} onChange={setPhoneCode} disabled={phoneBusy} autoFocus />
                    {phoneError && <p className="mb-4 text-sm text-red-600">{phoneError}</p>}
                    <Button type="submit" disabled={phoneBusy || phoneCode.length < 6}>
                      Log in
                    </Button>
                    <button
                      type="button"
                      onClick={() => {
                        setPhoneCodeSent(false)
                        setPhoneCode("")
                        setPhoneError(null)
                      }}
                      className="mt-4 block text-sm text-brand underline"
                    >
                      Use a different number
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handlePhoneRequest}>
                    <input
                      type="tel"
                      autoComplete="tel"
                      value={phone}
                      onChange={event => setPhone(event.target.value)}
                      className="mb-4 w-full rounded-md border-0 bg-neutral-100 p-4"
                      placeholder="Enter your phone number"
                      aria-label="Phone number"
                    />
                    {phoneError && <p className="mb-4 text-sm text-red-600">{phoneError}</p>}
                    <Button type="submit" disabled={phoneBusy || phone.length === 0}>
                      Send Code
                    </Button>
                  </form>
                )}
              </div>
            )}
          </div>
          <div className="flex-1 text-center">
            <div className="rounded-xl border border-neutral-500 px-4 py-12">
              <h1 className="mb-8 text-xl font-bold text-black">Register</h1>
              <div className="mb-6 text-lg font-bold">Need an Encircled account?</div>
              <Button className="mx-auto" onClick={() => navigate("/register/new")}>Register</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
